import React, { useMemo } from "react";

/**
 * iter88 · Shared padel-format score displays. Consumes the CANONICAL match
 * document that the referee scores against (via `useMatchSocket` or
 * `/api/public/matches`) — the same source everyone else reads from.
 *
 *   • <PadelLiveScore m={match} />
 *       Live view — sets, current-set games and current-game points
 *       (0/15/30/40/AD/DEUCE/TIEBREAK).
 *
 *   • <PadelFinalScore m={match} />
 *       Final view — set breakdown ("6-3, 7-5") + sets-to-sets summary
 *       ("Team A wins 2-0").
 */

/** Convert a raw game_a/game_b (0..3) into padel display strings, honouring
 *  deuce/advantage/tiebreak. Mirrors the compact backend `_compact_display`. */
const LADDER = ["0", "15", "30", "40"];
export function padelPointDisplay(tennisState) {
  const ts = tennisState || {};
  if (ts.in_tiebreak) {
    return { a: String(ts.tiebreak_a || 0), b: String(ts.tiebreak_b || 0), label: "TIEBREAK" };
  }
  if (ts.deuce) {
    if (ts.advantage === "a") return { a: "AD", b: "40", label: "AD A" };
    if (ts.advantage === "b") return { a: "40", b: "AD", label: "AD B" };
    return { a: "40", b: "40", label: "DEUCE" };
  }
  const ga = Math.min(3, Number(ts.game_a) || 0);
  const gb = Math.min(3, Number(ts.game_b) || 0);
  return { a: LADDER[ga], b: LADDER[gb], label: "" };
}

/** Extract finished sets, the current in-progress set and the current game
 *  score into a single, easy-to-render shape. */
export function derivePadelScore(m) {
  const setScores = Array.isArray(m?.set_scores) ? m.set_scores : [];
  const finished = setScores.filter((s) => !s.in_progress && ((s.a || 0) + (s.b || 0) > 0));
  const inProgress = setScores.find((s) => s.in_progress);
  // Sets won: prefer explicit totals from tennis_engine; fall back to derived count.
  let setsA =
    (m?.total_sets_won_a ?? null) !== null ? Number(m.total_sets_won_a) :
    (m?.tennis_state?.sets_won_a ?? null) !== null ? Number(m.tennis_state.sets_won_a) :
    finished.filter((s) => (s.a || 0) > (s.b || 0)).length;
  let setsB =
    (m?.total_sets_won_b ?? null) !== null ? Number(m.total_sets_won_b) :
    (m?.tennis_state?.sets_won_b ?? null) !== null ? Number(m.tennis_state.sets_won_b) :
    finished.filter((s) => (s.b || 0) > (s.a || 0)).length;
  // Current game points — prefer the backend-computed `tennis_display` shipped
  // on /api/public/matches (label + a + b already resolved), otherwise derive
  // from `tennis_state`.
  const pts = m?.tennis_display
    ? { a: String(m.tennis_display.a ?? "0"), b: String(m.tennis_display.b ?? "0"), label: m.tennis_display.label || "" }
    : padelPointDisplay(m?.tennis_state);
  return { finished, inProgress, setsA, setsB, pts };
}

/** Compact live scoreboard for the public landing / TV / broadcast tiles. */
export function PadelLiveScore({ m, teamAName, teamBName, dense }) {
  const { finished, inProgress, setsA, setsB, pts } = useMemo(() => derivePadelScore(m), [m]);
  const isSetBased = m?.format_type === "set_based" || Array.isArray(m?.set_scores);
  if (!isSetBased) {
    // Legacy fixed-format fallback — show total games (score_a already carries them).
    return (
      <div className="text-center" data-testid="padel-live-legacy">
        <div className="font-anton text-4xl text-white tabular-nums">
          {m?.score_a ?? 0}
          <span className="text-zinc-600 mx-2">·</span>
          {m?.score_b ?? 0}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">Games</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="padel-live">
      <div className="text-right">
        <div className={`font-anton uppercase truncate ${dense ? "text-sm" : "text-lg"} text-white`}>
          {teamAName || m?.team_a?.name || m?.team_a_name || "A"}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SetCol setsWon={setsA} finishedSets={finished.map((s) => s.a)} inProgress={inProgress?.a} />
        <span className="text-zinc-600">·</span>
        <SetCol setsWon={setsB} finishedSets={finished.map((s) => s.b)} inProgress={inProgress?.b} align="left" />
      </div>
      <div>
        <div className={`font-anton uppercase truncate ${dense ? "text-sm" : "text-lg"} text-white`}>
          {teamBName || m?.team_b?.name || m?.team_b_name || "B"}
        </div>
      </div>
      {/* Current game points (only when the match is actually running) */}
      {(pts.a !== "0" || pts.b !== "0" || pts.label) && (
        <div className="col-span-3 mt-2 flex items-center justify-center gap-3 text-[10px] uppercase tracking-widest">
          <span className="text-zinc-500">Current game</span>
          <span className="font-anton text-amber-300 text-lg tabular-nums">
            {pts.a}
            <span className="text-zinc-600 mx-1">·</span>
            {pts.b}
          </span>
          {pts.label && (
            <span className="text-[9px] uppercase font-bold text-red-300 tracking-widest">
              {pts.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** One team's set column: [set1_games][set2_games] · big SETS WON pill. */
function SetCol({ setsWon, finishedSets, inProgress, align = "right" }) {
  const games = [...finishedSets, ...(typeof inProgress === "number" ? [inProgress] : [])];
  return (
    <div className={`flex flex-col items-${align === "left" ? "start" : "end"} gap-0.5`}>
      <div className="flex items-baseline gap-1 tabular-nums">
        {games.map((g, i) => (
          <span
            key={i}
            className={`text-sm font-anton ${
              i === games.length - 1 && typeof inProgress === "number"
                ? "text-amber-300"
                : "text-zinc-300"
            }`}
          >
            {g}
          </span>
        ))}
      </div>
      <span className="text-2xl font-anton text-[#00A86B] leading-none tabular-nums" title="Sets won">
        {setsWon}
      </span>
    </div>
  );
}

/** Final-view score card for recent-results / finished tiles. */
export function PadelFinalScore({ m, teamAName, teamBName }) {
  const { setsA, setsB } = useMemo(() => derivePadelScore(m), [m]);
  const setScores = Array.isArray(m?.set_scores) ? m.set_scores.filter((s) => (s.a || 0) + (s.b || 0) > 0) : [];
  const aName = teamAName || m?.team_a?.name || m?.team_a_name || "A";
  const bName = teamBName || m?.team_b?.name || m?.team_b_name || "B";
  const aWon = setsA > setsB;
  const setLine = setScores.length > 0
    ? setScores.map((s) => `${s.a}-${s.b}`).join(", ")
    : `${m?.total_games_won_a ?? m?.score_a ?? 0}-${m?.total_games_won_b ?? m?.score_b ?? 0}`;
  return (
    <div className="space-y-1" data-testid="padel-final">
      <div className="grid grid-cols-[1fr_auto_1fr] items-baseline gap-2">
        <div
          className={`font-anton text-base uppercase truncate ${
            aWon ? "text-[#00A86B]" : "text-white"
          }`}
        >
          {aName}
        </div>
        <div className="font-anton text-3xl tabular-nums text-white">
          {setsA}
          <span className="text-zinc-600 mx-2">·</span>
          {setsB}
        </div>
        <div
          className={`font-anton text-base uppercase text-right truncate ${
            !aWon ? "text-[#00A86B]" : "text-white"
          }`}
        >
          {bName}
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 tabular-nums">
        Sets · {setLine}
        <span className="text-zinc-700 mx-2">|</span>
        {setsA === setsB ? (
          <span className="text-zinc-300">Draw {setsA}–{setsB}</span>
        ) : (
          <span className="text-zinc-300">
            {aWon ? aName : bName} wins {Math.max(setsA, setsB)}–{Math.min(setsA, setsB)}
          </span>
        )}
      </div>
    </div>
  );
}
